/**
 * App shell: brand corner, top bar (global search, theme switch, notifications,
 * user menu), sidebar, router outlet — ported from the mockups. Also hosts the
 * dirty-state navigation guard: leaving a page with unsaved edits asks first.
 */
import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useBlocker, useNavigate } from "react-router-dom";
import { CommentsPane, CommentsPaneProvider } from "./CommentsPane.tsx";
import { useQuery } from "@tanstack/react-query";
import { THEMES, type Theme, type SearchGroup, type Module } from "@crc/shared";
import { Avatar, DirtyContext, Icon, IconButton, Menu, useClickOutside, useDebounce, useDirtyRegistry, useOnline, type IconName } from "@crc/ui";
import { useAuth } from "../lib/auth.tsx";
import { useTheme } from "../lib/theme.tsx";
import { api } from "../lib/api.ts";
import { BarcodeScanButton } from "../pages/shared/Barcode.tsx";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  module?: Module;
  staffOnly?: boolean;
  countKey?: keyof Counts;
  end?: boolean;
}
interface Counts {
  companies?: number;
  ingredients?: number;
  requests?: number;
  inspections?: number;
}

const NAV: Array<{ label: string; items: NavItem[] }> = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: "dashboard", end: true },
      { to: "/desk", label: "My desk", icon: "users", staffOnly: true },
    ],
  },
  {
    label: "Certification",
    items: [
      { to: "/companies", label: "Companies", icon: "building", module: "companies", countKey: "companies" },
      { to: "/onboarding", label: "Onboarding queue", icon: "users", module: "companies", staffOnly: true },
      { to: "/facilities", label: "Facilities", icon: "factory", module: "locations" },
      { to: "/plants", label: "Plants", icon: "industry", module: "locations" },
      { to: "/ingredients", label: "Ingredients", icon: "flask", module: "ingredients", countKey: "ingredients" },
      { to: "/requests", label: "Requests", icon: "file", module: "ingredients", countKey: "requests" },
      { to: "/products", label: "Products", icon: "box", module: "products" },
      { to: "/authorizations", label: "Authorizations", icon: "pin", module: "locations" },
      { to: "/listings", label: "Listings", icon: "external", module: "companies" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/productions", label: "Productions", icon: "calendar", module: "productions" },
      { to: "/purchase-orders", label: "Purchase orders", icon: "file", module: "productions" },
      { to: "/events", label: "Events", icon: "users", module: "productions" },
      { to: "/schedule", label: "Schedule", icon: "clock", module: "inspections" },
      { to: "/inspections", label: "Inspections", icon: "clipboard", module: "inspections", countKey: "inspections" },
      { to: "/compensation", label: "Compensation", icon: "dollar", module: "inspections" },
      { to: "/seals", label: "Seals", icon: "award", module: "holograms" },
      { to: "/app", label: "Mashgiach app", icon: "phone", module: "inspections" },
      { to: "/scan", label: "Scan an item", icon: "flask", module: "ingredients" },
      { to: "/certificates", label: "Certificates", icon: "award", module: "certificates" },
      { to: "/compliance", label: "Compliance", icon: "shield", module: "compliance" },
    ],
  },
  {
    label: "Programmes",
    items: [
      { to: "/stam", label: "STaM", icon: "award", module: "stam" },
      { to: "/practitioners", label: "Practitioners", icon: "users", module: "stam" },
      { to: "/directory", label: "מפתח guide", icon: "book", module: "directory" },
      { to: "/wine", label: "Wine", icon: "droplet", module: "wine" },
      { to: "/esrogim", label: "Esrogim", icon: "wheat", module: "esrogim" },
    ],
  },
  {
    label: "Finance & Admin",
    items: [
      { to: "/billing", label: "Billing", icon: "card", module: "billing" },
      { to: "/membership", label: "Membership", icon: "users", module: "membership", staffOnly: true },
      { to: "/reports", label: "Reports", icon: "chart", module: "reports", staffOnly: true },
      { to: "/admin", label: "Admin Settings", icon: "settings", module: "settings", staffOnly: true },
    ],
  },
];

function ThemeSwitch() {
  const { theme, setTheme } = useTheme();
  const labels: Record<Theme, string> = { light: "Light", navy: "Navy", dark: "Dark", warm: "Warm" };
  return (
    <div className="theme-switch" role="group" aria-label="Theme">
      {THEMES.map((t) => (
        <button key={t} type="button" className={theme === t ? "active" : ""} onClick={() => setTheme(t)} aria-pressed={theme === t}>
          <span className={`dot dot-${t}`} />
          <span className="tlabel">{labels[t]}</span>
        </button>
      ))}
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounce(q.trim(), 200);
  const navigate = useNavigate();
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const { data, isFetching } = useQuery({ queryKey: ["search", debounced], queryFn: () => api.get<{ groups: SearchGroup[] }>(`/search?q=${encodeURIComponent(debounced)}&limit=5`), enabled: debounced.length >= 2, staleTime: 30_000 });
  const flat = useMemo(() => (data?.groups ?? []).flatMap((g) => g.hits), [data]);
  useEffect(() => setActive(0), [debounced]);
  const go = (href: string) => {
    setOpen(false);
    setQ("");
    navigate(href);
  };
  return (
    <div className="global-search" ref={ref}>
      <Icon name="search" size={16} />
      <input
        type="search"
        value={q}
        placeholder="Search companies, ingredients (VKD), products, inspections… (Enter for all results)"
        aria-label="Global search"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, flat.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter" && flat[active]) go(flat[active]!.href);
          else if (e.key === "Enter" && q.trim().length >= 2) go(`/search?q=${encodeURIComponent(q.trim())}`);
          else if (e.key === "Escape") setOpen(false);
        }}
      />
      <span className="search-scan"><BarcodeScanButton label="" onScan={(code) => go(`/scan?upc=${encodeURIComponent(code)}`)} /></span>
      {open && debounced.length >= 2 && (
        <div className="search-pop" role="listbox">
          {isFetching && !data && <div className="muted small" style={{ padding: 12 }}>Searching…</div>}
          {data && flat.length === 0 && <div className="muted small" style={{ padding: 12 }}>No matches for “{debounced}”. A barcode or VKD resolves directly.</div>}
          {data?.groups.map((g) => (
            <div key={g.type}>
              <div className="search-group-label">{g.label}</div>
              {g.hits.map((h) => {
                const idx = flat.indexOf(h);
                return (
                  <div key={h.id} className={`search-hit${idx === active ? " active" : ""}`} role="option" aria-selected={idx === active} onMouseEnter={() => setActive(idx)} onClick={() => go(h.href)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t">{h.title}</div>
                      {h.subtitle && <div className="d">{h.subtitle}</div>}
                    </div>
                    {h.status && <span className="badge badge-neutral">{h.status.replace(/_/g, " ")}</span>}
                  </div>
                );
              })}
              {g.more && <div className="menu-note">More results in the {g.label.toLowerCase()} list…</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { me, can, isStaff, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const dirty = useDirtyRegistry();
  const online = useOnline();
  const navigate = useNavigate();
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty.any && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("You have unsaved changes. Leave this page and discard them?")) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker]);
  const { data: counts } = useQuery({ queryKey: ["nav-counts"], queryFn: () => api.get<Counts>("/dashboard/nav-counts").catch(() => ({}) as Counts), staleTime: 60_000, enabled: isStaff });

  const visible = (it: NavItem) => {
    if (it.staffOnly && !isStaff) return false;
    if (it.module && !can(it.module, "read")) return false;
    if (it.to === "/app" && me?.userType !== "mashgiach" && !isStaff) return false;
    return true;
  };
  const fmt = (n?: number) => (n === undefined ? null : n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : String(n));

  return (
    <DirtyContext.Provider value={dirty}>
      <CommentsPaneProvider>
      <div className="app">
        <a className="skip-link" href="#main">Skip to content</a>
        <NavLink to="/" className="brand" aria-label="CRC Kashrus home">
          <img className="brand-logo" src="/crc-logo.png" alt="CRC Kashrus" />
          <div className="brand-text">
            <span>Certification Platform</span>
          </div>
        </NavLink>
        <header className="topbar">
          <IconButton icon="menu" label="Menu" className="menu-toggle" aria-expanded={open} aria-controls="sidebar" onClick={() => setOpen(true)} />
          {isStaff ? <GlobalSearch /> : <div className="global-search" />}
          <div className="topbar-spacer" />
          {!online && (
            <span className="sync-pill offline" title="Drafts are kept on this device and sent when you have signal">
              <Icon name="wifi-off" size={14} /> Offline
            </span>
          )}
          <ThemeSwitch />
          <IconButton icon="bell" label="Notifications" pip={false} onClick={() => navigate("/profile")} />
          <Menu
            align="right"
            label="Account menu"
            trigger={
              <button type="button" className="user" aria-haspopup="menu">
                <Avatar name={me?.name} />
                <div className="user-meta">
                  <b>{me?.name}</b>
                  <span>{me?.role.name}{me?.companyName ? ` · ${me.companyName}` : ""}</span>
                </div>
              </button>
            }
            items={[
              { label: "My profile & preferences", icon: "user", to: "/profile" },
              ...(isStaff ? [{ label: "Admin settings", icon: "settings" as IconName, to: "/admin" }] : []),
              { sep: true, label: "" },
              { label: "Sign out", icon: "logout", onClick: () => void signOut(), danger: true },
            ]}
          />
        </header>
        <aside className={`sidebar${open ? " open" : ""}`} id="sidebar" aria-label="Main navigation">
          {NAV.map((group) => {
            const items = group.items.filter(visible);
            if (!items.length) return null;
            return (
              <div className="nav-group" key={group.label}>
                <div className="nav-group-label">{group.label}</div>
                {items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`} onClick={() => setOpen(false)}>
                    <Icon name={it.icon} /> {it.label}
                    {it.countKey && counts?.[it.countKey] !== undefined && <span className="count">{fmt(counts[it.countKey])}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </aside>
        {open && <div className="scrim show" onClick={() => setOpen(false)} />}
        <main className="main" id="main" tabIndex={-1}>
          <div className="main-row">
            <div className="content">
              <Outlet />
            </div>
            <CommentsPane />
          </div>
        </main>
      </div>
      </CommentsPaneProvider>
    </DirtyContext.Provider>
  );
}
